import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const LINKS = [
  { label: "Why This Matters", href: "#why" },
  { label: "Our Vision", href: "#vision" },
  { label: "How It Works", href: "/how-it-works", route: true },
  { label: "Founder", href: "#founder" },
  { label: "Connect", href: "#connect" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (link) => {
    setOpen(false);
    if (link.route) {
      navigate(link.href);
      window.scrollTo({ top: 0 });
      return;
    }
    // hash link
    if (location.pathname !== "/") {
      navigate("/" + link.href);
      return;
    }
    const el = document.querySelector(link.href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-forest/90 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between h-20">
        <button
          data-testid="nav-logo"
          onClick={() => go({ href: "/", route: true })}
          className="flex items-center gap-3 text-cream text-left leading-none"
        >
          <img
            src="/nkw-logo-256.png"
            alt="Ngā Kākano Wāhine"
            className="h-10 w-10 sm:h-11 sm:w-11 rounded-full object-cover ring-1 ring-gold/40"
          />
          <span className="font-serif-display text-lg sm:text-2xl tracking-tight hidden sm:block">
            Ngā Kākano Wāhine
          </span>
        </button>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.replace(/[#/]/g, "")}`}
              onClick={() => go(l)}
              className="text-cream/85 hover:text-cream text-sm font-light link-underline"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-partner-btn"
            onClick={() => go({ href: "#connect" })}
            className="border border-gold/60 text-cream text-sm px-5 py-2 hover:bg-gold hover:text-forest transition-colors duration-300"
          >
            Register Your Interest
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-cream"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-forest/95 backdrop-blur-md"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l)}
                  className="text-cream/90 text-left font-light text-lg"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
